/**
 * SizilPro — Proposal Builder shared module types
 *
 * File: src/modules/proposal-builder/types.ts
 */

import type {
  Json,
  Tables,
  TablesInsert,
  TablesUpdate,
} from "@/lib/database.types";

/* ============================================================
   Shared JSON helper types
============================================================ */

export type JsonObject = { [key: string]: Json | undefined };

/* ============================================================
   Core enums / unions
============================================================ */

export type ClientType = string;

export type PriceUnit =
  | "per_clean"
  | "per_hour"
  | "per_sqft"
  | "per_visit"
  | "fixed";

export type BuilderMode = "prebuilt" | "drag_drop";

export type FixedPagePolicy =
  | "template_defined"
  | "brand_defined"
  | "mixed";

export type StaticPageKind =
  | "cover_letter"
  | "process"
  | "about"
  | "terms"
  | "testimonial"
  | "custom";

export type StaticPageLayoutPreset =
  | "standard"
  | "hero"
  | "split"
  | "image_left"
  | "image_right"
  | "custom";

export type BrandAssetRole =
  | "primary_logo"
  | "secondary_logo"
  | "badge"
  | "cover_image"
  | "background_image"
  | "content_image"
  | "watermark"
  | "other";

export type BrandAssetPlacementPreset =
  | "none"
  | "top_left"
  | "top_center"
  | "top_right"
  | "left_column"
  | "right_column"
  | "full_bleed"
  | "background"
  | "footer"
  | "custom";

export type BrandAssetPageScope =
  | "all"
  | "cover_only"
  | "cover_letter_only"
  | "services_only"
  | "static_pages_only"
  | "custom";

export type BrandFontSourceType = "built_in" | "uploaded";
export type BrandFontFileFormat = "ttf" | "otf" | "woff" | "woff2";
export type BrandFontStyle = "normal" | "italic";

export type BrandingLayoutPreset =
  | "classic"
  | "modern"
  | "minimal"
  | "bold"
  | "custom";

export type DefaultBuilderExperience = "prebuilt" | "drag_drop" | "both";

export type ProposalStatus =
  | "draft"
  | "sent"
  | "viewed"
  | "signed"
  | "declined"
  | "expired";

export type SignatureRequestStatus =
  | "pending"
  | "sent"
  | "viewed"
  | "signed"
  | "declined"
  | "expired";

export type TemplatePageType =
  | "cover"
  | "cover_letter"
  | "line_items"
  | "static_page"
  | "custom"
  | "closing"
  | "signature";

export type TemplatePageSectionType =
  | "hero"
  | "content"
  | "media"
  | "services"
  | "summary"
  | "footer"
  | "custom";

export type TemplatePageLayoutMode =
  | "stack"
  | "two_column"
  | "three_column"
  | "split_left"
  | "split_right"
  | "grid"
  | "custom";

export type TemplatePageElementType =
  | "text"
  | "rich_text"
  | "image"
  | "logo"
  | "divider"
  | "spacer"
  | "service_table"
  | "pricing_summary"
  | "signature_block"
  | "badge"
  | "company_info"
  | "project_summary"
  | "static_content"
  | "custom";

/* ============================================================
   Labels / constants
============================================================ */

export const PRICE_UNIT_LABELS: Record<PriceUnit, string> = {
  per_clean: "per clean",
  per_hour: "per hour",
  per_sqft: "per sqft",
  per_visit: "per visit",
  fixed: "fixed",
};

/* ============================================================
   Shared builder / document types
============================================================ */

export interface BuilderDocumentMargins extends JsonObject {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
}

export interface BuilderDocumentSettings extends JsonObject {
  pageSize?: "letter" | "a4";
  pageOrientation?: "portrait" | "landscape";
  margins?: BuilderDocumentMargins;
}

export interface ProposalDocumentLayoutSection extends JsonObject {
  id?: string;
  title?: string;
  type?: TemplatePageSectionType | string;
  order?: number;
  layoutMode?: TemplatePageLayoutMode | string;
  settings?: JsonObject;
  elements?: JsonObject[];
}

export interface ProposalDocumentLayoutPage extends JsonObject {
  id?: string;
  title?: string;
  type?: TemplatePageType | string;
  order?: number;
  settings?: JsonObject;
  sections?: ProposalDocumentLayoutSection[];
}

export interface ProposalDocumentLayout extends JsonObject {
  pages: ProposalDocumentLayoutPage[];
}

/* ============================================================
   Brand setup
============================================================ */

export type BuiltInFontPreference =
  | "inter"
  | "roboto"
  | "lato"
  | "open-sans";

export type TypographyFontRole = "heading" | "body";

export interface BrandTypographySelection extends JsonObject {
  source?: BrandFontSourceType;
  builtInFont?: BuiltInFontPreference | null;
  uploadedFontId?: string | null;
  uploadedFontName?: string | null;
}

export type TypographySelection = BrandTypographySelection;

export interface BrandTypographyPreferences extends JsonObject {
  heading?: BrandTypographySelection;
  body?: BrandTypographySelection;
}

export interface BrandPreviewPreferences extends JsonObject {
  typography?: BrandTypographyPreferences;
}

export type BrandSettingsRow = Tables<"brand_settings">;
export type BrandSettingsInsertRow = TablesInsert<"brand_settings">;
export type BrandSettingsUpdateRow = TablesUpdate<"brand_settings">;

export interface BrandSettings
  extends Omit<
    BrandSettingsRow,
    | "default_builder_experience"
    | "branding_layout_preset"
    | "preview_preferences"
    | "proposal_image_rules"
    | "color_palette"
  > {
  default_builder_experience: DefaultBuilderExperience | null;
  branding_layout_preset: BrandingLayoutPreset | null;
  preview_preferences: BrandPreviewPreferences;
  proposal_image_rules: Record<string, Json>;
  color_palette: Record<string, Json>;
}

export type BrandSettingsInsert = Omit<
  BrandSettingsInsertRow,
  "default_builder_experience" | "branding_layout_preset" | "preview_preferences"
> & {
  default_builder_experience?: DefaultBuilderExperience | null;
  branding_layout_preset?: BrandingLayoutPreset | null;
  preview_preferences?: BrandPreviewPreferences;
};

export type BrandSettingsUpdate = Omit<
  BrandSettingsUpdateRow,
  "default_builder_experience" | "branding_layout_preset" | "preview_preferences"
> & {
  default_builder_experience?: DefaultBuilderExperience | null;
  branding_layout_preset?: BrandingLayoutPreset | null;
  preview_preferences?: BrandPreviewPreferences;
};

export type BrandAssetRow = Tables<"brand_assets">;
export type BrandAssetInsertRow = TablesInsert<"brand_assets">;
export type BrandAssetUpdateRow = TablesUpdate<"brand_assets">;

export interface BrandAsset
  extends Omit<BrandAssetRow, "asset_role" | "placement_preset" | "page_scope"> {
  asset_role: BrandAssetRole;
  placement_preset: BrandAssetPlacementPreset | null;
  page_scope: BrandAssetPageScope | null;
}

export type BrandAssetInsert = Omit<
  BrandAssetInsertRow,
  "asset_role" | "placement_preset" | "page_scope" | "client_id"
> & {
  client_id?: string;
  asset_role: BrandAssetRole;
  placement_preset?: BrandAssetPlacementPreset | null;
  page_scope?: BrandAssetPageScope | null;
};

export type BrandAssetUpdate = Omit<
  BrandAssetUpdateRow,
  "asset_role" | "placement_preset" | "page_scope"
> & {
  asset_role?: BrandAssetRole;
  placement_preset?: BrandAssetPlacementPreset | null;
  page_scope?: BrandAssetPageScope | null;
};

export type BrandFontRow = Tables<"brand_fonts">;
export type BrandFontInsertRow = TablesInsert<"brand_fonts">;
export type BrandFontUpdateRow = TablesUpdate<"brand_fonts">;

export interface BrandFont
  extends Omit<BrandFontRow, "source_type" | "file_format" | "font_style"> {
  source_type: BrandFontSourceType | null;
  file_format: BrandFontFileFormat | null;
  font_style: BrandFontStyle | null;
}

export type BrandFontInsert = Omit<
  BrandFontInsertRow,
  "source_type" | "file_format" | "font_style" | "client_id"
> & {
  client_id?: string;
  source_type?: BrandFontSourceType | null;
  file_format?: BrandFontFileFormat | null;
  font_style?: BrandFontStyle | null;
};

export type BrandFontUpdate = Omit<
  BrandFontUpdateRow,
  "source_type" | "file_format" | "font_style"
> & {
  source_type?: BrandFontSourceType | null;
  file_format?: BrandFontFileFormat | null;
  font_style?: BrandFontStyle | null;
};

/* ============================================================
   Static pages
============================================================ */

export type StaticPageRow = Tables<"static_pages">;
export type StaticPageInsertRow = TablesInsert<"static_pages">;
export type StaticPageUpdateRow = TablesUpdate<"static_pages">;

export interface StaticPage
  extends Omit<StaticPageRow, "page_kind" | "layout_preset" | "style_overrides"> {
  page_kind: StaticPageKind;
  layout_preset: StaticPageLayoutPreset;
  style_overrides: Record<string, Json>;
}

export type StaticPageInsert = Omit<
  StaticPageInsertRow,
  "page_kind" | "layout_preset"
> & {
  page_kind?: StaticPageKind;
  layout_preset?: StaticPageLayoutPreset;
};

export type StaticPageUpdate = Omit<
  StaticPageUpdateRow,
  "page_kind" | "layout_preset"
> & {
  page_kind?: StaticPageKind;
  layout_preset?: StaticPageLayoutPreset;
};

/* ============================================================
   Service catalog
============================================================ */

export type ServiceItemRow = Tables<"service_items">;
export type ServiceItemInsertRow = TablesInsert<"service_items">;
export type ServiceItemUpdateRow = TablesUpdate<"service_items">;

export interface ServiceItem extends Omit<ServiceItemRow, "price_unit"> {
  price_unit: PriceUnit;
  category: string | null;
}

export type ServiceItemInsert = Omit<ServiceItemInsertRow, "price_unit"> & {
  price_unit?: PriceUnit;
};

export type ServiceItemUpdate = Omit<ServiceItemUpdateRow, "price_unit"> & {
  price_unit?: PriceUnit;
};

/* ============================================================
   Template relational children
============================================================ */

export type TemplateServiceRow = Tables<"template_services">;
export type TemplateServiceInsertRow = TablesInsert<"template_services">;
export type TemplateServiceUpdateRow = TablesUpdate<"template_services">;

export interface TemplateService extends Omit<TemplateServiceRow, "price_unit"> {
  price_unit: PriceUnit | null;
}

export type TemplateServiceInsert = Omit<
  TemplateServiceInsertRow,
  "price_unit"
> & {
  price_unit?: PriceUnit | null;
};

export type TemplateServiceUpdate = Omit<
  TemplateServiceUpdateRow,
  "price_unit"
> & {
  price_unit?: PriceUnit | null;
};

export type TemplateStaticPageRow = Tables<"template_static_pages">;
export type TemplateStaticPageInsert = TablesInsert<"template_static_pages">;
export type TemplateStaticPageUpdate = TablesUpdate<"template_static_pages">;

export interface TemplateStaticPage extends TemplateStaticPageRow {}

/* ============================================================
   Template builder structures
============================================================ */

export type TemplatePageRow = Tables<"template_pages">;
export type TemplatePageInsertRow = TablesInsert<"template_pages">;
export type TemplatePageUpdateRow = TablesUpdate<"template_pages">;

export interface TemplatePage
  extends Omit<TemplatePageRow, "page_type" | "settings" | "client_id"> {
  client_id?: string;
  page_type: TemplatePageType;
  settings: Record<string, Json>;
}

export type TemplatePageInsert = Omit<
  TemplatePageInsertRow,
  "page_type" | "client_id"
> & {
  client_id?: string;
  page_type: TemplatePageType;
};

export type TemplatePageUpdate = Omit<
  TemplatePageUpdateRow,
  "page_type" | "client_id"
> & {
  client_id?: string;
  page_type?: TemplatePageType;
};

export type TemplatePageSectionRow = Tables<"template_page_sections">;
export type TemplatePageSectionInsertRow = TablesInsert<"template_page_sections">;
export type TemplatePageSectionUpdateRow = TablesUpdate<"template_page_sections">;

export interface TemplatePageSection
  extends Omit<
    TemplatePageSectionRow,
    "section_type" | "layout_mode" | "settings"
  > {
  section_type: TemplatePageSectionType;
  layout_mode: TemplatePageLayoutMode;
  settings: Record<string, Json>;
}

export type TemplatePageSectionInsert = Omit<
  TemplatePageSectionInsertRow,
  "section_type" | "layout_mode"
> & {
  section_type: TemplatePageSectionType;
  layout_mode?: TemplatePageLayoutMode;
};

export type TemplatePageSectionUpdate = Omit<
  TemplatePageSectionUpdateRow,
  "section_type" | "layout_mode"
> & {
  section_type?: TemplatePageSectionType;
  layout_mode?: TemplatePageLayoutMode;
};

export type TemplatePageElementRow = Tables<"template_page_elements">;
export type TemplatePageElementInsertRow = TablesInsert<"template_page_elements">;
export type TemplatePageElementUpdateRow = TablesUpdate<"template_page_elements">;

export interface TemplatePageElement
  extends Omit<
    TemplatePageElementRow,
    "element_type" | "content" | "style" | "bindings" | "visibility_rules"
  > {
  element_type: TemplatePageElementType;
  content: Record<string, Json>;
  style: Record<string, Json>;
  bindings: Record<string, Json>;
  visibility_rules: Record<string, Json>;
}

export type TemplatePageElementInsert = Omit<
  TemplatePageElementInsertRow,
  "element_type"
> & {
  element_type: TemplatePageElementType;
};

export type TemplatePageElementUpdate = Omit<
  TemplatePageElementUpdateRow,
  "element_type"
> & {
  element_type?: TemplatePageElementType;
};

/* ============================================================
   Legacy compatibility structures used by templates/proposals
============================================================ */

export interface TemplateLineItem extends JsonObject {
  service_item_id: string | null;
  name: string;
  description: string | null;
  price: number | null;
  price_unit: PriceUnit | null;
  frequency: string | null;
  duration_hours: number | null;
  sort_order: number;
}

/* ============================================================
   Proposal templates
============================================================ */

export type ProposalTemplateRow = Tables<"proposal_templates">;
export type ProposalTemplateInsertRow = TablesInsert<"proposal_templates">;
export type ProposalTemplateUpdateRow = TablesUpdate<"proposal_templates">;

export interface ProposalTemplate
  extends Omit<
    ProposalTemplateRow,
    | "builder_mode"
    | "fixed_page_policy"
    | "document_settings"
    | "layout_json"
    | "client_type"
    | "default_line_items"
  > {
  builder_mode: BuilderMode;
  fixed_page_policy: FixedPagePolicy;
  document_settings: BuilderDocumentSettings;
  layout_json: ProposalDocumentLayout;
  client_type: ClientType | null;
  default_line_items: TemplateLineItem[];

  template_services?: TemplateService[];
  template_static_pages?: TemplateStaticPage[];
  template_pages?: TemplatePage[];
}

export type ProposalTemplateInsert = Omit<
  ProposalTemplateInsertRow,
  | "builder_mode"
  | "fixed_page_policy"
  | "document_settings"
  | "layout_json"
  | "client_type"
  | "default_line_items"
> & {
  builder_mode?: BuilderMode;
  fixed_page_policy?: FixedPagePolicy;
  document_settings?: BuilderDocumentSettings;
  layout_json?: ProposalDocumentLayout;
  client_type?: ClientType | null;

  template_services?: Array<{
    service_item_id?: string | null;
    service_name: string;
    service_description?: string | null;
    base_price?: number | null;
    price_unit?: PriceUnit | null;
    quantity?: number | null;
    sort_order?: number | null;
  }>;

  template_static_pages?: Array<{
    static_page_id?: string | null;
    title: string;
    content_html?: string | null;
    page_order?: number | null;
  }>;

  default_line_items?: TemplateLineItem[];
  default_static_page_ids?: string[];
};

export type ProposalTemplateUpdate = Omit<
  ProposalTemplateUpdateRow,
  | "builder_mode"
  | "fixed_page_policy"
  | "document_settings"
  | "layout_json"
  | "client_type"
  | "default_line_items"
> & {
  builder_mode?: BuilderMode;
  fixed_page_policy?: FixedPagePolicy;
  document_settings?: BuilderDocumentSettings;
  layout_json?: ProposalDocumentLayout;
  client_type?: ClientType | null;

  template_services?: Array<{
    service_item_id?: string | null;
    service_name: string;
    service_description?: string | null;
    base_price?: number | null;
    price_unit?: PriceUnit | null;
    quantity?: number | null;
    sort_order?: number | null;
  }>;

  template_static_pages?: Array<{
    static_page_id?: string | null;
    title: string;
    content_html?: string | null;
    page_order?: number | null;
  }>;

  default_line_items?: TemplateLineItem[];
  default_static_page_ids?: string[];
};

/* ============================================================
   Proposals
============================================================ */

export type ProposalRow = Tables<"proposals">;
export type ProposalInsertRow = TablesInsert<"proposals">;
export type ProposalUpdateRow = TablesUpdate<"proposals">;

export interface Proposal extends ProposalRow {
  status: ProposalStatus;
}

export type ProposalInsert = ProposalInsertRow;
export type ProposalUpdate = ProposalUpdateRow;

export type ProposalLineItemRow = Tables<"proposal_line_items">;
export type ProposalLineItemInsertRow = TablesInsert<"proposal_line_items">;
export type ProposalLineItemUpdateRow = TablesUpdate<"proposal_line_items">;

export interface ProposalLineItem extends Omit<ProposalLineItemRow, "price_unit"> {
  price_unit: PriceUnit | null;
}

export type ProposalLineItemInsert = Omit<
  ProposalLineItemInsertRow,
  "price_unit"
> & {
  price_unit?: PriceUnit | null;
};

export type ProposalLineItemUpdate = Omit<
  ProposalLineItemUpdateRow,
  "price_unit"
> & {
  price_unit?: PriceUnit | null;
};

export type ProposalStaticPage = Tables<"proposal_static_pages">;
export type ProposalStaticPageInsert = TablesInsert<"proposal_static_pages">;
export type ProposalStaticPageUpdate = TablesUpdate<"proposal_static_pages">;

/* ============================================================
   Signature / provider-agnostic helpers
============================================================ */

export type SignatureRequestRow = Tables<"signature_requests">;
export type SignatureRequestInsert = TablesInsert<"signature_requests">;
export type SignatureRequestUpdate = TablesUpdate<"signature_requests">;

export interface SignatureRequest extends SignatureRequestRow {
  status: SignatureRequestStatus;
}

export interface ProposalSigningLink {
  url: string | null;
  providerRequestId: string | null;
  status: ProposalStatus | SignatureRequestStatus | null;
}

export interface SignatureLifecycleState {
  status: ProposalStatus | SignatureRequestStatus | null;
  sentAt: string | null;
  viewedAt: string | null;
  signedAt: string | null;
  declinedAt: string | null;
  expiresAt: string | null;
  declineReason: string | null;
}

/* ============================================================
   Dashboard / reporting helpers
============================================================ */

export interface PipelineStats {
  total: number;
  draft: number;
  sent: number;
  viewed: number;
  signed: number;
  declined: number;
  expired: number;
}

/* ============================================================
   Setup / AI helper payloads
============================================================ */

export interface CoverLetterAnswers {
  company: string;
  differentiator: string;
  quality: string;
  idealClient: string;
  tone: "Professional and formal" | "Warm and personal" | "Confident and direct";
}

/* ============================================================
   Hydrated helper shapes
============================================================ */

export interface ProposalFull extends Proposal {
  template?: ProposalTemplate | null;
  proposal_line_items?: ProposalLineItem[];
  proposal_static_pages?: ProposalStaticPage[];
  signature_requests?: SignatureRequest[];
}